"use client";

import { cva, VariantProps } from "class-variance-authority";
import useEmblaCarousel, { type UseEmblaCarouselType } from "embla-carousel-react";
import { WheelGesturesPlugin, WheelGesturesPluginOptions } from "embla-carousel-wheel-gestures";
import * as React from "react";

import { cn } from "@/lib/ui/cn";

import { icon } from "./icons";

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

export type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: "horizontal" | "vertical";
  setApi?: (api: CarouselApi) => void;
  withWheelGestures?: boolean;
  wheelGesturesOptions?: Partial<WheelGesturesPluginOptions>;
};

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

function useCarousel() {
  const context = React.useContext(CarouselContext);

  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />");
  }

  return context;
}

const Carousel = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & CarouselProps
>(
  (
    {
      orientation = "horizontal",
      opts,
      setApi,
      plugins,
      className,
      children,
      withWheelGestures,
      wheelGesturesOptions,
      ...rest
    },
    ref,
  ) => {
    const combinedPlugins = React.useMemo(() => {
      if (withWheelGestures) {
        return [...(plugins || []), WheelGesturesPlugin(wheelGesturesOptions)];
      }
      return plugins;
    }, [plugins, withWheelGestures, wheelGesturesOptions]);

    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === "horizontal" ? "x" : "y",
      },
      combinedPlugins,
    );
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);

    const onSelect = React.useCallback((api: CarouselApi) => {
      if (!api) {
        return;
      }

      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    }, []);

    const scrollPrev = React.useCallback(() => {
      api?.scrollPrev();
    }, [api]);

    const scrollNext = React.useCallback(() => {
      api?.scrollNext();
    }, [api]);

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          scrollPrev();
        } else if (event.key === "ArrowRight") {
          event.preventDefault();
          scrollNext();
        }
      },
      [scrollPrev, scrollNext],
    );

    React.useEffect(() => {
      if (!api || !setApi) {
        return;
      }

      setApi(api);
    }, [api, setApi]);

    React.useEffect(() => {
      if (!api) {
        return;
      }

      onSelect(api);
      api.on("reInit", onSelect);
      api.on("select", onSelect);

      return () => {
        api?.off("select", onSelect);
      };
    }, [api, onSelect]);

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          api: api,
          opts,
          orientation: orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
          scrollPrev,
          scrollNext,
          canScrollPrev,
          canScrollNext,
        }}
      >
        <div
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={cn("relative", className)}
          role="region"
          aria-roledescription="carousel"
          {...(rest as Omit<React.HTMLAttributes<HTMLDivElement>, keyof CarouselProps>)}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    );
  },
);
Carousel.displayName = "Carousel";

const CarouselContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { rootClassName?: string }
>(({ className, rootClassName, ...props }, ref) => {
  const { carouselRef, orientation } = useCarousel();

  return (
    <div ref={carouselRef} className={cn("overflow-hidden", rootClassName)}>
      <div
        ref={ref}
        className={cn("flex", orientation === "horizontal" ? "-ml-4" : "-mt-4 flex-col", className)}
        {...props}
      />
    </div>
  );
});
CarouselContent.displayName = "CarouselContent";

const CarouselItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const { orientation } = useCarousel();

    return (
      <div
        ref={ref}
        role="group"
        aria-roledescription="slide"
        className={cn(
          "min-w-0 shrink-0 grow-0 basis-full",
          orientation === "horizontal" ? "pl-4" : "pt-4",
          className,
        )}
        {...props}
      />
    );
  },
);
CarouselItem.displayName = "CarouselItem";

const ArrowBack = icon("ArrowBack");

const carouselButtonVariants = cva(
  cn(
    "absolute cursor-pointer rounded-full bg-white p-3 disabled:cursor-default disabled:bg-white/50",
  ),
  {
    variants: { variant: { default: "", outline: "border-2 border-[#000003]" } },
    defaultVariants: { variant: "default" },
  },
);

const CarouselPrevious = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> & VariantProps<typeof carouselButtonVariants>
>(({ className, variant = "default", ...props }, ref) => {
  const { orientation, scrollPrev, canScrollPrev } = useCarousel();

  return (
    <button
      ref={ref}
      className={cn(
        carouselButtonVariants({ variant }),
        orientation === "horizontal"
          ? "top-1/2 -left-12 -translate-y-1/2"
          : "-top-12 left-1/2 -translate-x-1/2 rotate-90",
        className,
      )}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      {...props}
    >
      <ArrowBack className="text-neutral-darkest size-6" />
      <span className="sr-only">Previous slide</span>
    </button>
  );
});
CarouselPrevious.displayName = "CarouselPrevious";

const ArrowForward = icon("ArrowForward");

const CarouselNext = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> & VariantProps<typeof carouselButtonVariants>
>(({ className, variant = "default", ...props }, ref) => {
  const { orientation, scrollNext, canScrollNext } = useCarousel();

  return (
    <button
      ref={ref}
      className={cn(
        carouselButtonVariants({ variant }),
        orientation === "horizontal"
          ? "top-1/2 -right-12 -translate-y-1/2"
          : "-bottom-12 left-1/2 -translate-x-1/2 rotate-90",
        className,
      )}
      disabled={!canScrollNext}
      onClick={scrollNext}
      {...props}
    >
      <ArrowForward className="text-neutral-darkest size-6" />
      <span className="sr-only">Next slide</span>
    </button>
  );
});
CarouselNext.displayName = "CarouselNext";

const CarouselIndicators = React.forwardRef<HTMLDivElement, React.ComponentProps<"div">>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("gap-gap-element-2xs py-padding-element-lg flex w-full", className)}
        {...props}
      />
    );
  },
);
CarouselIndicators.displayName = "CarouselIndicators";

const CarouselIndicator = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> & { isActive: boolean; index: number }
>(({ className, isActive, index, ...props }, ref) => {
  const { api } = useCarousel();

  return (
    <button
      ref={ref}
      className={cn(
        "h-1 w-full max-w-[53px] cursor-pointer rounded-sm bg-white/60 transition-colors",
        isActive && "bg-emerald-light",
        className,
      )}
      onClick={() => api?.scrollTo(index)}
      aria-label={`Go to slide ${index + 1}`}
      {...props}
    />
  );
});
CarouselIndicator.displayName = "CarouselIndicator";

export {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselIndicator,
  CarouselIndicators,
  CarouselItem,
  CarouselNext,
  type CarouselOptions,
  CarouselPrevious,
  useCarousel,
};
